import React from 'react';
import { partial } from 'lodash';

import Box from 'components/Box';
import Button from 'components/clickables/Button';
import Icon from 'components/Icon';
import Text from 'components/Text';

import Form from './Form/Form';
import {
  canEditTaxClassification,
  isAContractor,
  isSubmitButtonDisabled,
} from './helpers';
import HiringSourceSelect from './HiringSourceSelect';

interface Props {
  // State props
  hasPaidPayrollRunsForJob: boolean;
  hasNoActivePayments: boolean;
  fourTensRuleEnabled: boolean;
  hasITIN: boolean;
  jobTaxClassification: string | null;
  contractorType: string | null;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  wageAttributes: unknown[];
  hireDate: unknown;
  sendSms: boolean;
  packet: string;
  level: string;
  includedInPayroll: boolean;
  requestInformation: string | null;
  errors: Record<string, unknown>;
  canRequestInformation: boolean;
  isEligibleForPayroll: boolean | null;
  userId: string | number;
  firstNameMaximum: number;
  hiredFrom: string;
  submitting: boolean;

  // Props from parent
  editingUser: unknown;
  canViewWages: boolean;
  hasOnboardingDocsAccess: boolean;
  isPayrollEnrolled: boolean;
  isPayrollSetup: boolean;
  isUpdatingTeamMember: boolean;
  numberOfDocumentsInContractorTemplate: number;
  numberOfDocumentsInEmployeeTemplate: number;
  newHireOnboardingActivated: boolean;
  hiringSourceExperiment: boolean;
  user: unknown;
  accessLevelOptions: unknown[];

  // Event handlers
  handleChange: (field: string, value: unknown) => void;
  handleBlur: (field: string) => void;
  handlePacketChange: (value: string) => void;
  handleJobTaxClassificationChange: (value: string) => void;
  handleContractorTypeChange: (value: string) => void;
  setFieldValue: (key: string, value: unknown) => void;
  handleMoveBack: () => void;
  handleSubmitUser: () => void;
  handleSaveAndClose: () => void;
  handleSaveAndAdd: () => void;
  invalidPayrollInfo: (user: unknown) => boolean;
  validateUserPayrollEligibility: () => void;
  cxEl: (element: string) => string;

  // Mobile/tablet flag
  mobileTablet: boolean;
}

const FormView = ({
  // State props
  hasPaidPayrollRunsForJob,
  hasNoActivePayments,
  fourTensRuleEnabled,
  hasITIN,
  jobTaxClassification,
  contractorType,
  firstName,
  lastName,
  phone,
  email,
  wageAttributes,
  hireDate,
  sendSms,
  packet,
  level,
  includedInPayroll,
  requestInformation,
  errors,
  canRequestInformation,
  isEligibleForPayroll,
  userId,
  firstNameMaximum,
  hiredFrom,
  submitting,

  // Props from parent
  editingUser,
  canViewWages,
  hasOnboardingDocsAccess,
  isPayrollEnrolled,
  isPayrollSetup,
  isUpdatingTeamMember,
  numberOfDocumentsInContractorTemplate,
  numberOfDocumentsInEmployeeTemplate,
  newHireOnboardingActivated,
  hiringSourceExperiment,
  user,
  accessLevelOptions,

  // Event handlers
  handleChange,
  handleBlur,
  handlePacketChange,
  handleJobTaxClassificationChange,
  handleContractorTypeChange,
  setFieldValue,
  handleMoveBack,
  handleSubmitUser,
  handleSaveAndClose,
  handleSaveAndAdd,
  invalidPayrollInfo,
  validateUserPayrollEligibility,
  cxEl,

  // Mobile/tablet flag
  mobileTablet,
}: Props) => {
  if (isPayrollEnrolled) {
    validateUserPayrollEligibility();
  }

  return (
    <Box p={mobileTablet ? 15 : 32} relative h="100%" borderbox>
      <Box row mb={24}>
        {!editingUser && (
          <Box pointer onClick={handleMoveBack} vcenter mr={8}>
            <Icon type="chevronLeft" size={16} color="blue" />
          </Box>
        )}
        <Box vcenter>
          {editingUser ? (
            <Text fs24 fw600 i18n="edit_team.individual_drawer.title" />
          ) : (
            <Text fs24 fw600 i18n="add_team.individual_drawer.title" />
          )}
        </Box>
      </Box>

      <Form
        hasPaidPayrollRunsForJob={hasPaidPayrollRunsForJob}
        hasNoActivePayments={hasNoActivePayments}
        isPayrollUpdating={!!editingUser}
        isPayrollSetup={!!isPayrollSetup}
        handleChange={handleChange}
        handleBlur={handleBlur}
        handlePacketChange={handlePacketChange}
        handleJobTaxClassificationChange={handleJobTaxClassificationChange}
        jobTaxClassification={jobTaxClassification}
        handleContractorTypeChange={handleContractorTypeChange}
        contractorType={contractorType}
        fourTensRuleEnabled={fourTensRuleEnabled}
        canEditTaxClassification={canEditTaxClassification({
          editingUser,
          hasNoActivePayments,
          hasITIN,
        })}
        hasITIN={hasITIN}
        setFieldValue={setFieldValue}
        isAContractor={isAContractor(jobTaxClassification)}
        cxEl={cxEl}
        canViewWages={canViewWages}
        isPayrollEnrolled={isPayrollEnrolled}
        hasOnboardingDocsAccess={hasOnboardingDocsAccess}
        newHireOnboardingActivated={newHireOnboardingActivated}
        numberOfDocumentsInContractorTemplate={
          numberOfDocumentsInContractorTemplate
        }
        numberOfDocumentsInEmployeeTemplate={
          numberOfDocumentsInEmployeeTemplate
        }
        firstName={firstName}
        lastName={lastName}
        phone={phone}
        email={email}
        wageAttributes={wageAttributes}
        hireDate={hireDate}
        sendSms={sendSms}
        packet={packet}
        level={level}
        includedInPayroll={includedInPayroll}
        requestInformation={requestInformation}
        errors={errors}
        canRequestInformation={canRequestInformation}
        invalidPayrollInfo={invalidPayrollInfo}
        isEligibleForPayroll={isEligibleForPayroll}
        userId={userId}
        firstNameMaximum={firstNameMaximum}
        hiringSourceExperiment={hiringSourceExperiment}
        user={user}
        accessLevelOptions={accessLevelOptions}
      />

      {hiringSourceExperiment && (
        <HiringSourceSelect
          hiredFromValue={hiredFrom}
          onHiredFromChange={partial(handleChange, 'hiredFrom')}
          payrollEnrolled={isPayrollEnrolled}
        />
      )}

      {isPayrollEnrolled ? (
        <Box row bottom={0} left={0} w="100%" borderbox hright pb16>
          <Box w={235} data-testid={cxEl('continue-button')}>
            <Button
              fullWidth
              size={mobileTablet ? 'small' : 'medium'}
              disabled={isSubmitButtonDisabled({
                firstName,
                errors,
                isPayrollEnrolled,
                canRequestInformation,
                isEligibleForPayroll,
                invalidPayrollInfo,
                user,
                requestInformation,
              })}
              theme="primary"
              onClick={handleSubmitUser}
              loading={isUpdatingTeamMember || submitting}
            >
              <Text
                fw600
                fs={mobileTablet ? 16 : 18}
                i18n="edit_team.individual_drawer.continue"
              />
            </Button>
          </Box>
        </Box>
      ) : (
        <Box row bottom={0} left={0} w="100%" borderbox>
          <Box minw={235} grow mr={12}>
            <Button
              fullWidth
              size={mobileTablet ? 'small' : 'medium'}
              disabled={isSubmitButtonDisabled({
                firstName,
                errors,
                isPayrollEnrolled,
                canRequestInformation,
                isEligibleForPayroll,
                invalidPayrollInfo,
                user,
                requestInformation,
              })}
              theme="secondary-blue"
              onClick={handleSaveAndClose}
              loading={submitting}
            >
              <Text
                fw600
                fs={mobileTablet ? 16 : 18}
                i18n="add_team.individual_drawer.save_and_finish"
              />
            </Button>
          </Box>
          <Box minw={235} grow>
            <Button
              fullWidth
              size={mobileTablet ? 'small' : 'medium'}
              disabled={isSubmitButtonDisabled({
                firstName,
                errors,
                isPayrollEnrolled,
                canRequestInformation,
                isEligibleForPayroll,
                invalidPayrollInfo,
                user,
                requestInformation,
              })}
              theme="primary"
              onClick={handleSaveAndAdd}
              loading={submitting}
            >
              <Text
                fw600
                fs={mobileTablet ? 16 : 18}
                i18n="add_team.individual_drawer.save_and_add"
              />
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default FormView;
